import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import type { AddPreview, LibrarySearch, MbResult } from "../api.js";
import { RatingStrip } from "../design/RatingStrip.js";
import { useMobile } from "../useMobile.js";
import styles from "./Search.module.css";

/**
 * Search: what you have, then what exists (design/README.md §8).
 *
 * The two halves are separated by a full-width ink band rather than a heading, and the design is
 * explicit about why: *"This separation must be unmissable — it is what prevents accidental
 * duplicates."* Everything below the band is a stranger, and adding one writes to the library.
 *
 * So the band is not decoration and never collapses. It renders even when MusicBrainz returned
 * nothing, because the absence of results below it is itself the answer to "is this already
 * mine?" — and a missing band would make an empty MusicBrainz half look like an empty library.
 */

export interface SearchProps {
  q: string;
  onQuery: (q: string) => void;
  library: LibrarySearch | null;
  /** null while the network half has not answered; `[]` is a real, different answer. */
  mb: MbResult[] | null;
  mbLoading: boolean;
  mbError: string | null;
  onRetryMb: () => void;

  /** The add flow. `preview` is non-null once a record has been opened. */
  preview: AddPreview | null;
  previewFor: string | null;
  previewLoading: boolean;
  onPreview: (result: MbResult) => void;
  onClosePreview: () => void;
  onChooseRelease: (releaseMbid: string) => void;
  onAdd: (bonus: number[], startRating: boolean) => void;
  adding: boolean;
}

type Row =
  | { kind: "song"; id: number; label: string; sub: string; to: string; score: number | null }
  | { kind: "album"; id: number; label: string; sub: string; to: string; score: number | null }
  | { kind: "artist"; id: number; label: string; sub: string; to: string; score: number | null };

const duration = (ms: number | null) =>
  ms === null ? "—" : `${Math.floor(ms / 60000)}:${String(Math.floor((ms % 60000) / 1000)).padStart(2, "0")}`;

export function Search({
  q,
  onQuery,
  library,
  mb,
  mbLoading,
  mbError,
  onRetryMb,
  preview,
  previewFor,
  previewLoading,
  onPreview,
  onClosePreview,
  onChooseRelease,
  onAdd,
  adding,
}: SearchProps) {
  const input = useRef<HTMLInputElement>(null);
  const [cursor, setCursor] = useState(0);

  /**
   * One flat list of the library half, so `↑ ↓` crosses the section boundaries the eye already
   * crosses. Three separately-navigable lists would mean three cursors and a tab key.
   */
  const rows = useMemo<Row[]>(() => {
    if (!library) return [];
    return [
      ...library.albums.map((a): Row => ({
        kind: "album",
        id: a.id,
        label: a.title,
        sub: `${a.artist} · ALBUM · ${a.coreCount} CORE · ${a.ratedCore >= a.coreCount && a.coreCount > 0 ? "COMPLETE" : `${a.ratedCore} / ${a.coreCount} RATED`}`,
        to: `/album/${a.id}`,
        score: null,
      })),
      ...library.songs.map((s): Row => ({
        kind: "song",
        id: s.id,
        label: s.title,
        sub: `${s.artist} · SONG${s.trackNo === null ? "" : ` · TRACK ${s.trackNo}`} · ${s.album}`,
        to: `/album/${s.albumId}`,
        score: s.score,
      })),
      ...library.artists.map((a): Row => ({
        kind: "artist",
        id: a.id,
        label: a.name,
        sub: `ARTIST · ${a.songCount} SONGS`,
        to: `/rankings`,
        score: null,
      })),
    ];
  }, [library]);

  useEffect(() => setCursor(0), [q]);

  // Before paint, like every other global key handler here: a search box that is on screen and
  // not yet listening loses the first keystrokes of a fast typist.
  useLayoutEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        if (preview) return onClosePreview();
        onQuery("");
        input.current?.focus();
        return;
      }
      if (preview) return;
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setCursor((c) => Math.min(rows.length - 1, c + 1));
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setCursor((c) => Math.max(0, c - 1));
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [rows.length, onQuery, preview, onClosePreview]);

  return (
    <div className={styles.page}>
      <div className={styles.field}>
        <input
          ref={input}
          className={styles.input}
          type="search"
          value={q}
          autoFocus
          aria-label="Search"
          placeholder="SEARCH YOUR LIBRARY AND MUSICBRAINZ"
          onChange={(e) => onQuery(e.target.value)}
        />
        <div className={styles.hints}>ESC CLEARS · ↑↓ MOVE · ⏎ OPEN</div>
      </div>

      {q.trim() === "" ? (
        <div className={styles.idle} data-testid="search-idle">
          TYPE TO SEARCH. YOUR LIBRARY ANSWERS INSTANTLY; MUSICBRAINZ TAKES A MOMENT.
        </div>
      ) : (
        <>
          <div className={styles.sectionHead}>YOUR LIBRARY</div>
          {rows.length === 0 ? (
            <div className={styles.none} data-testid="library-none">
              NOTHING IN YOUR LIBRARY MATCHES.
            </div>
          ) : (
            <div className={styles.well} data-testid="library-results">
              {rows.map((row, i) => (
                <Link
                  key={`${row.kind}-${row.id}`}
                  to={row.to}
                  className={styles.row}
                  data-cursor={i === cursor || undefined}
                  data-testid="library-row"
                  onMouseEnter={() => setCursor(i)}
                >
                  <div className={styles.rowLabel}>{row.label}</div>
                  <div className={styles.rowSub}>{row.sub}</div>
                  {/* `newsprint`, not `well`: these rows are light. On a dark ground the strip paints set
                      cells light and unset cells dark, so a light row inverted it — a 9 read as a 1
                      and an unrated song read as a full strip. */}
                  {/* Wrapped so the mobile layout can drop it: at 390px the row is a stacked
                      title over its kind, and a display strip beside them is clutter next to the
                      number it repeats. */}
                  <span className={styles.rowStrip}>
                    {row.kind === "song" && <RatingStrip value={row.score} ground="newsprint" />}
                  </span>
                  <div className={styles.rowScore} data-unrated={row.score === null || undefined}>
                    {row.kind === "song" ? (row.score ?? "UNR") : ""}
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* The band. Full width, ink, and it does not collapse — see the note at the top. */}
          <div className={styles.band} data-testid="mb-band">
            <span className={styles.bandTitle}>MUSICBRAINZ</span>
            <span className={styles.bandNote}>NOT IN YOUR LIBRARY · ADDING PULLS THE FULL TRACK LIST</span>
          </div>

          {mbError ? (
            <div className={styles.mbError} data-testid="mb-error">
              {/* Never an empty list: "MusicBrainz is unreachable" and "this record does not
                  exist" are opposite statements, and confusing them is how a duplicate gets added. */}
              MUSICBRAINZ DID NOT ANSWER. THIS IS NOT THE SAME AS THE RECORD NOT EXISTING.
              <button type="button" className={styles.retry} onClick={onRetryMb}>
                Try again
              </button>
            </div>
          ) : mb === null || mbLoading ? (
            <div className={styles.none} data-testid="mb-waiting">
              LOOKING…
            </div>
          ) : mb.length === 0 ? (
            <div className={styles.none} data-testid="mb-none">
              MUSICBRAINZ HAS NOTHING UNDER THAT NAME.
            </div>
          ) : (
            <div className={styles.cards} data-testid="mb-results">
              {mb.map((result) => (
                <div key={result.mbid} className={styles.card} data-testid="mb-card">
                  <div className={styles.cardMain}>
                    <div className={styles.cardTitle}>{result.title}</div>
                    <div className={styles.cardArtist}>
                      {result.artist}
                      {result.disambiguation && <span className={styles.disambiguation}> · {result.disambiguation}</span>}
                    </div>
                    <div className={styles.cardDetail}>
                      {[result.primaryType?.toUpperCase() ?? "RELEASE", result.firstReleaseDate?.slice(0, 4) ?? "NO YEAR"]
                        .filter(Boolean)
                        .join(" · ")}
                      <br />
                      {result.mbid}
                    </div>
                  </div>
                  <button
                    type="button"
                    className={styles.addButton}
                    data-testid="mb-add"
                    disabled={previewLoading && previewFor === result.mbid}
                    onClick={() => onPreview(result)}
                  >
                    {previewLoading && previewFor === result.mbid ? "Looking…" : "Add →"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {preview && (
        <AddPanel
          preview={preview}
          adding={adding}
          onClose={onClosePreview}
          onChooseRelease={onChooseRelease}
          onAdd={onAdd}
        />
      )}
    </div>
  );
}

/**
 * The add preview: what adding this will do, stated before it happens.
 *
 * The count is the sentence's subject, not a footnote, because it is the number the user is
 * actually agreeing to. Chips are per-track and default to CORE — a record's own tracks are what
 * it is, and the chips exist for the two live cuts on the end.
 */
function AddPanel({
  preview,
  adding,
  onClose,
  onChooseRelease,
  onAdd,
}: {
  preview: AddPreview;
  adding: boolean;
  onClose: () => void;
  onChooseRelease: (releaseMbid: string) => void;
  onAdd: (bonus: number[], startRating: boolean) => void;
}) {
  const [bonus, setBonus] = useState<number[]>([]);
  const total = preview.tracks.length;
  const core = total - bonus.length;

  /**
   * Desktop starts rating; mobile queues instead — design/README.md §8, and the reason given is
   * that on mobile "the user is probably walking". Measured on the viewport rather than the user
   * agent, because a narrow window on a desktop is the same situation.
   *
   * This used to carry its own 760px threshold. There is one line between the two designs (§13),
   * and a second number deciding a second thing near it is how a screen ends up half mobile.
   */
  const narrow = useMobile();

  return (
    <div className={styles.panel} data-testid="add-panel">
      <div className={styles.panelHead}>
        <div>
          <div className={styles.panelTitle}>{preview.title}</div>
          <div className={styles.panelArtist}>{preview.artist}</div>
          <div className={styles.panelMeta}>{preview.releaseGroupMbid}</div>
        </div>
        <button type="button" className={styles.close} aria-label="Close" onClick={onClose}>
          ×
        </button>
      </div>

      {preview.alreadyInLibrary && (
        <div className={styles.already} data-testid="already-in-library">
          {/* Leading with it, because this is the exact moment a duplicate gets created. */}
          <b>{preview.alreadyInLibrary.title}</b> is already in your library. Adding this would make a second copy.
          <Link to="/album/$albumId" params={{ albumId: String(preview.alreadyInLibrary.id) }} className={styles.goTo}>
            Go to the one you have →
          </Link>
        </div>
      )}

      {preview.needsChoice && (
        <div className={styles.releases} data-testid="release-choice">
          <span className={styles.releasesLabel}>TWO EDITIONS ARE CLOSE. PICK ONE:</span>
          {preview.releases.slice(0, 4).map((r) => (
            <button
              key={r.mbid}
              type="button"
              className={styles.release}
              data-active={r.mbid === preview.chosenReleaseMbid || undefined}
              onClick={() => onChooseRelease(r.mbid)}
            >
              {r.title} · {r.trackCount} TRACKS · {r.date?.slice(0, 4) ?? "NO YEAR"}
            </button>
          ))}
        </div>
      )}

      <div className={styles.statement}>
        <div className={styles.statementCount} data-testid="add-count">
          {total}
        </div>
        <div className={styles.statementText}>
          Adds {total} songs to your library. {core} core, {bonus.length} bonus. All {total} will need rating, and
          rating starts on track 1 as soon as you add.
        </div>
      </div>

      <div className={styles.trackList} data-testid="add-tracks">
        {preview.tracks.map((t) => {
          const isBonus = bonus.includes(t.position);
          return (
            <div key={`${t.discNo}-${t.position}`} className={styles.track} data-bonus={isBonus || undefined}>
              <span className={styles.trackNo}>{t.position}</span>
              <span className={styles.trackTitle}>{t.title}</span>
              <span className={styles.trackDur}>{duration(t.durationMs)}</span>
              <button
                type="button"
                className={styles.chip}
                data-bonus={isBonus || undefined}
                data-testid="track-chip"
                aria-label={`Mark ${t.title} as ${isBonus ? "core" : "bonus"}`}
                onClick={() =>
                  setBonus((prev) => (prev.includes(t.position) ? prev.filter((p) => p !== t.position) : [...prev, t.position]))
                }
              >
                {isBonus ? "BONUS" : "CORE"}
              </button>
            </div>
          );
        })}
      </div>

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.primary}
          disabled={adding}
          data-testid="add-and-rate"
          onClick={() => onAdd(bonus, !narrow)}
        >
          {adding ? "Adding…" : narrow ? "Add & queue →" : "Add & start rating →"}
        </button>
        <button type="button" className={styles.secondary} disabled={adding} onClick={() => onAdd(bonus, false)}>
          Add only
        </button>
      </div>
    </div>
  );
}
